import {
  DirectoryDepartment,
  DirectoryMarketingContentSection,
  DirectorySettings,
  EligibilitySource,
} from '@accredible-frontend-v2/models';
import { AccredibleDialogService } from '@accredible-frontend-v2/new-components/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DirectoriesApiService } from '@dashboard/domain';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, exhaustMap } from 'rxjs/operators';
import { ConfirmBulkDialogComponent } from '../components/confirm-bulk-dialog/confirm-bulk-dialog.component';
import { DirectorySettingsApiService } from '../services/directory-settings.service';

export interface DirectorySettingsState {
  directorySettings: DirectorySettings;
  marketingContent: DirectoryMarketingContentSection[];
  groups: EligibilitySource[];
  departments: DirectoryDepartment[];

  action: DirectorySettingsStateAction;
  payload: any;
  error: any;
}

export enum DirectorySettingsStateAction {
  NO_ACTION,

  DIRECTORY_SETTINGS_LOADED,
  DIRECTORY_SETTINGS_UPDATED,
  DIRECTORY_SETTINGS_UPDATED_WITH_BULK_OPERATION,

  MARKETING_CONTENT_LOADED,

  HAS_ERROR,
}

const initialStateHandling: Partial<DirectorySettingsState> = {
  action: DirectorySettingsStateAction.NO_ACTION,
  payload: null,
  error: null,
};

export const initialState: DirectorySettingsState = {
  directorySettings: null,
  marketingContent: null,
  groups: null,
  departments: null,

  ...(<DirectorySettingsState>initialStateHandling),
};

@Injectable()
export class DirectorySettingsStore extends ComponentStore<DirectorySettingsState> {
  private readonly _directorySettingsApi = inject(DirectorySettingsApiService);
  private readonly _directoriesApi = inject(DirectoriesApiService);
  private readonly _dialogService = inject(AccredibleDialogService);

  constructor() {
    super(initialState);
  }

  readonly setInitialStateHandling = this.updater((state: DirectorySettingsState) => {
    return {
      ...state,
      ...(<DirectorySettingsState>initialStateHandling),
    };
  });

  readonly loadDirectorySettings = this.effect((action$: Observable<{ directoryId: number }>) => {
    return action$.pipe(
      exhaustMap((action) => {
        this.setInitialStateHandling();

        const settings$ = this._directoriesApi
          .loadDirectorySettings(action.directoryId)
          .pipe(catchError((error) => this._handleError(error)));
        const groups$ = this._directorySettingsApi.loadDirectoryGroups().pipe();
        const departments$ = this._directorySettingsApi
          .loadDirectoryDepartments()
          .pipe(catchError((error) => this._handleError(error)));

        return forkJoin([settings$, groups$, departments$]).pipe(
          tapResponse({
            next: ([settings, groups, departments]) => {
              this.setState((state) => ({
                ...state,
                directorySettings: { ...settings },
                groups: [...groups],
                departments: [...departments],
                ...(<DirectorySettingsState>initialStateHandling),
                action: DirectorySettingsStateAction.DIRECTORY_SETTINGS_LOADED,
              }));
            },
            error: (error) => this._handleError(error),
          }),
        );
      }),
    );
  });

  readonly loadDirectoryMarketingContent = this.effect(
    (action$: Observable<{ directoryId: number }>) => {
      return action$.pipe(
        exhaustMap((action) => {
          return this._directoriesApi.loadDirectoryMarketingContent(action.directoryId).pipe(
            tapResponse({
              next: (marketingContent) => {
                this.setState((state) => ({
                  ...state,
                  marketingContent,
                  action: DirectorySettingsStateAction.MARKETING_CONTENT_LOADED,
                }));
              },
              error: (error) => this._handleError(error),
            }),
          );
        }),
      );
    },
  );

  readonly editDirectorySettings = this.effect(
    (
      action$: Observable<{
        directoryId: number;
        directorySettings: DirectorySettings;
        bulkOperation?: boolean;
      }>,
    ) => {
      return action$.pipe(
        exhaustMap((action) => {
          this.setInitialStateHandling();

          return this._directorySettingsApi
            .editDirectorySettings(action.directoryId, action.directorySettings)
            .pipe(
              tapResponse({
                next: (directorySettings) => {
                  this.setState((state) => ({
                    ...state,
                    directorySettings: { ...directorySettings },
                    ...(<DirectorySettingsState>initialStateHandling),
                    action: action.bulkOperation
                      ? DirectorySettingsStateAction.DIRECTORY_SETTINGS_UPDATED_WITH_BULK_OPERATION
                      : DirectorySettingsStateAction.DIRECTORY_SETTINGS_UPDATED,
                  }));
                },
                error: (error: HttpErrorResponse) => {
                  if (error?.status === 409) {
                    this._dialogService.open(ConfirmBulkDialogComponent, {
                      width: '400px',
                    });
                  }
                  return this._handleError(error);
                },
              }),
            );
        }),
      );
    },
  );

  readonly directorySettings$ = this.select((state) => state.directorySettings);
  readonly marketingContent$ = this.select((state) =>
    state.marketingContent?.length > 0 ? state.marketingContent : null,
  );
  readonly evaluatorProfilesCount$ = this.select(
    (state) => state.directorySettings.spotlight_directory_evaluator_profiles_count,
  );

  private _handleError(
    error: any,
    action: DirectorySettingsStateAction = DirectorySettingsStateAction.HAS_ERROR,
  ): Observable<never> {
    this.setState((state) => ({
      ...state,
      action,
    }));

    return throwError(() => error);
  }
}
